import Grid from '@mui/material/Grid'
import Fab from '@mui/material/Fab';
import CssBaseline from '@mui/material/CssBaseline';
import SellIcon from '@mui/icons-material/SellRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded'
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded'

import { useRef, useState } from 'react'
import { useEffect } from 'react'
import { LogApi } from '../api/LogApi'
import { ApplicationState } from '../interface/ApplicationState'
import { ProfitSummary } from '../interface/ProfitSummary'
import { REFRESH_INTERVAL } from '../const'

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { OnboardModal } from '../components/modals/Onboard/OnboardModal';
import { TrialOverModal } from '../components/modals/TrialOver/TrialOverModal';
import { ProfitCard } from './dashboardv2/ProfitCard';
import { AssetCard } from './dashboardv2/AssetCard';
import { TotalAssets } from './dashboardv2/TotalAssets';
import { useDispatch, useSelector } from 'react-redux';
import { CurrentFunds } from './dashboardv2/CurrentFunds';
import { ChartV2Section } from './dashboardv2/ChartV2Section';
import { OrdersV2Section } from './dashboardv2/OrdersV2Section';
import { BuySellModal } from '../components/modals/BuySellModal/BuySellModal';
import { getSummaryBegin, validateApiKey } from '../redux/actions/userActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import { dashboardMutedTextSx } from './dashboardv2/dashboardStyles';

export const DashboardV2 = () =>
{
  const theme = useTheme()
  const user = useSelector(({ userStore }: ApplicationState) => userStore.user)
  const isGettingUserProfile = useSelector(({ userStore }: ApplicationState) => userStore.isGettingUserProfile)
  const subscriptionStatus = useSelector((state: ApplicationState) => state.billing.subscriptionStatus)
  const summary = useSelector((state: ApplicationState) => state.userStore.summary)
  const isGettingSummary = useSelector((state: ApplicationState) => state.userStore.isGettingSummary)
  const dispatch = useDispatch()
  const refreshRef = useRef<NodeJS.Timer>()

  const [ profitSummary, setProfitSummary ] = useState<ProfitSummary[] | undefined>(undefined)
  const [ loading, setLoading ] = useState(false)
  const [ showSellModal, setShowSellModal ] = useState(false)

  const isApiKeyValid = useSelector((state: ApplicationState) => state.userStore.isApiKeyValid)
  const apiKeyError = useSelector((state: ApplicationState) => state.userStore.apiKeyError)

  const getProfitSummary = async () =>
  {
    try
    {
      const logApi = new LogApi()
      const profitSummaryData = await logApi.getProfitSummary()
      setProfitSummary(profitSummaryData)
    }
    catch(error)
    {
      console.error("Error getting profit summary")
    }

  }

  useEffect(() => {

    getAllDashboardData()
    setRefresh()

    return function()
    {
      clearInterval(refreshRef.current)
    }

  }, [ ])

  useEffect(() => {

    getAllDashboardData()

  }, [ user.tradingPair ])

  const setRefresh = () =>
  {
    refreshRef.current = setInterval(() => {

      getAllDashboardData()

    }, REFRESH_INTERVAL) // Refresh every 10 mins
  }

  const getAllDashboardData = async () =>
  {
    if(user.tradingPair) dispatch(getSummaryBegin(user.tradingPair, true))
    if(!isApiKeyValid) dispatch(validateApiKey())

    setLoading(true)
    await getProfitSummary()
    setLoading(false)
  }

  return(

    <Box
      sx={{
        minHeight: "93vh",
        m: { xs: 1.5, md: 2 },
        p: { xs: 2, md: 3 },
        borderRadius: "20px",
        position: "relative",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, #06111f 0%, #091829 55%, #050c16 100%)`
            : `linear-gradient(180deg, #f3f8ff 0%, #ebf3ff 45%, #ffffff 100%)`,
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
      }}
    >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background:
          theme.palette.mode === "dark"
            ? `radial-gradient(circle at top left, ${alpha("#22d3ee", 0.16)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#7c3aed", 0.12)}, transparent 28%), radial-gradient(circle at bottom left, ${alpha("#38bdf8", 0.1)}, transparent 22%)`
            : `radial-gradient(circle at top left, ${alpha("#60a5fa", 0.2)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#0ea5e9", 0.12)}, transparent 30%)`,
      }}
    />
    <CssBaseline />

    { subscriptionStatus?.mode === 'free' && <TrialOverModal open handleClose={() => {} }/>}
    { !isGettingUserProfile && !loading && !isGettingSummary && !isApiKeyValid && <OnboardModal open handleClose={() => {}} /> }
    
      { isApiKeyValid && <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>

        <Grid item xs={12}>
          <Box
            sx={{
              p: { xs: 2.25, md: 3.5 },
              borderRadius: "16px",
              border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha("#102946", 0.94)} 0%, ${alpha("#081320", 0.96)} 100%)`
                  : `linear-gradient(135deg, ${alpha("#ffffff", 0.94)} 0%, ${alpha("#e6f0ff", 0.92)} 100%)`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 22px 60px ${alpha("#01050d", 0.45)}`
                  : `0 22px 60px ${alpha("#9db3d1", 0.2)}`,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} lg={7}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<BoltRoundedIcon />}
                      label={subscriptionStatus?.mode === "paid" || subscriptionStatus?.mode === "admin" ? "Trading Enabled" : "Dashboard Live"}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: alpha("#34d399", theme.palette.mode === "dark" ? 0.18 : 0.14),
                        color: "text.primary",
                      }}
                    />
                    <Chip
                      label={user.tradingPair || "No pair selected"}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.12),
                        color: "text.primary",
                      }}
                    />
                    <Chip
                      label={user.chartInterval || "No interval"}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.68),
                        color: "text.primary",
                      }}
                    />
                  </Stack>

                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 2.2, ...dashboardMutedTextSx(theme) }}>
                      AlgoTrader Dashboard
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: { xs: 34, md: 52 }, lineHeight: 0.98, maxWidth: 700 }}>
                      Trade intelligence with a stronger command-center feel.
                    </Typography>
                  </Box>

                  <Typography sx={{ maxWidth: 640, fontSize: 16, ...dashboardMutedTextSx(theme) }}>
                    Monitor profit, positions, chart signals, and order flow from one dark-first workspace. Light mode keeps the same structure with softer surfaces.
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.heroMetric(theme)}>
                      <AutoGraphRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Net worth</Typography>
                      <Typography sx={{ fontSize: 24 }}>
                        ${summary?.totalAssets?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.heroMetric(theme)}>
                      <CandlestickChartRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Position value</Typography>
                      <Typography sx={{ fontSize: 24 }}>
                        ${summary?.tradingPairSummary?.estValueInUsd?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.heroMetric(theme)}>
                      <SellIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Subscription</Typography>
                      <Typography sx={{ fontSize: 24, textTransform: "capitalize" }}>
                        {subscriptionStatus?.mode || "trial"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
        <Grid container spacing={2}>

          {/* Row 1 */}
          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            <ProfitCard loading={loading || isGettingSummary} profitSummary={profitSummary} />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            <AssetCard 
             assetName={summary?.tradingPairSummary.asset || "Unknown"}
             summary={summary}
             summaryItem={summary?.tradingPairSummary} 
             loading={loading || isGettingSummary} />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={4} xl={4} container direction="column" spacing={2}>

            <Grid item>
              <TotalAssets loading={loading|| isGettingSummary} summary={summary} user={user} />
            </Grid>
          
            <Grid item>
              <CurrentFunds loading={loading || isGettingSummary} summary={summary} user={user} />
            </Grid>

          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
            <ChartV2Section loading={loading || isGettingSummary} user={user}  />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            <OrdersV2Section marketId={user.tradingPair} />
          </Grid>


        </Grid>
        </Grid>

      { showSellModal && summary && user && user.tradingPair && <BuySellModal maxBuy={1000} side="sell" waitForBearSignal maxSell={Number(summary?.tradingPairSummary.estValueInUsd.toFixed(2))} open={showSellModal} handleClose={() => setShowSellModal(false)} tradingPair={user.tradingPair} /> }

      <Fab variant="extended" style={styles.fab} onClick={() => setShowSellModal(true)}>
        <SellIcon sx={{ mr: 1 }} /> Sell
      </Fab>

      <Snackbar
      open={!!apiKeyError} 
      onClose={() => {}} autoHideDuration={3000}>
        <Alert variant='filled' severity="error" >
        {apiKeyError}
      </Alert>
      </Snackbar>

      </Grid> }
    </Box>
  
  )

}

export const styles = {
  card: {
    height: 220,
    padding: 16,
  },
  fab: { 
    position: "fixed" as "fixed",
    margin: 0, 
    top: "auto", 
    right: 32, 
    bottom: 32, 
    left: "auto" 
  },
  heroMetric: (theme: any) => ({
    p: 2,
    borderRadius: "12px",
    display: "grid",
    gap: 0.5,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.5),
  }),
  loading: {
    float: "right" as "right"
  },
}
